import React from 'react';

import Tabs from '@/components/Tabs';
import { getSelectedTab, useSeedTabs } from '@/sections/seeds/config';
import { SeedType } from '@/sections/seeds/types';
import { KeyboardListersId } from '@/types';

/**
 * Props for SeedsHeader component
 */
interface SeedsHeaderProps {
  /** Current active seed type tab */
  activeTab: SeedType;
  /** Callback when tab is changed */
  onTabChange: (tab: SeedType) => void;
}

/**
 * Seeds section header with tab navigation
 *
 * Displays:
 * - Tab navigation for different seed types (domains, IPs, web apps, preseeds)
 *
 * **Performance Optimized**: React.memo prevents re-renders when props unchanged
 *
 * @example
 * ```tsx
 * <SeedsHeader
 *   activeTab={type}
 *   onTabChange={setType}
 * />
 * ```
 */
export const SeedsHeader: React.FC<SeedsHeaderProps> = React.memo(
  ({ activeTab, onTabChange }) => {
    // Use centralized tab configuration (static labels, no counts)
    const tabOptions = useSeedTabs();

    const selectedTab = getSelectedTab(tabOptions, activeTab);

    return (
      <Tabs
        id={KeyboardListersId.SEEDS_MAIN_TABS}
        variant="primary"
        tabs={tabOptions}
        selected={selectedTab}
        onSelect={tabOption => {
          onTabChange(tabOption.value as SeedType);
        }}
      />
    );
  }
);

SeedsHeader.displayName = 'SeedsHeader';
